// @ts-strict-ignore
import { CompressionSimpleTextValueVO, MetaElementUsageIdentityVO, MetaElementUsageVO, MetaElementVO, TableTextValueVO, TextureAreaUsageVO, TextureAreaVO, TextValueVO } from '@shared/vo/meta-element.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Vector3 } from 'three';
import { Connection } from '@3d-scene/model3d/connection';
import { ArticleUtils } from '@article/util/article-utils';
import { ConnectionCubeSearchRequirements } from '@article/components/connections/connection-creation/search/connection-cube-search-requirements';
import { ArticleCreationManager } from './article-creation.manager';
import { ContainerService } from '@3d-scene/model3d/container.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { AlertButton } from '@shared/alert/alertButton';
import { SuitableConnectionsVO } from '../model/vo/suitable-connections.vo';
import { TextTextureProcessor } from '@3d-scene/processor/text-texture.processor';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '../../../constants/meta-element-behavior-property-def';
import { NumberUtils } from '@shared/util/number-utils';
import { ArrayUtils } from '@shared/util/array-utils';
import { Injectable, OnDestroy } from '@angular/core';
import { Log } from '@shared/debug/log';
import { Subscription } from 'rxjs';
import { ArticleCreationService } from '../service/article-creation.service';
import { SelectionModelService } from '../service/selection-model.service';
import { ProjectMetaElementService } from '@project/manager/project-meta-element.service';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { AccessoryService, ConnectionSearchResult } from '@article/service/accessory.service';
import { ConnectionInfoService } from '@3d-scene/model3d/service/connection-info.service';

class ConversionData {
  public rColor: number = 0;
  public gColor: number = 0;
  public bColor: number = 0;

  public maxAllowedMultilines: number = 1;
  public isHasPreviousMergedUsageId: boolean = false;
  public isHasNextMergedUsageId: boolean = false;
  public textValue: TextValueVO = null;

  public usageIdentity: MetaElementUsageIdentityVO;

  public static createFromTextureAreaUsage(usage: TextureAreaUsageVO): ConversionData {
    const data: ConversionData = new ConversionData();
    data.bColor = usage.bColor;
    data.gColor = usage.gColor;
    data.rColor = usage.rColor;
    data.textValue = usage.textValue;
    data.usageIdentity = usage.identity;
    data.isHasPreviousMergedUsageId = !!usage.previousMergedUsageId;
    data.isHasNextMergedUsageId = !!usage.nextMergedUsageId;
    return data;
  }

  public fillTextureAreaUsage(usage: TextureAreaUsageVO, newMaxAllowedMultilines: number, isMergeAllowed: boolean): void {
    usage.bColor = this.bColor;
    usage.gColor = this.gColor;
    usage.rColor = this.gColor;
    usage.textValue = this.getTextValueToFill(newMaxAllowedMultilines, isMergeAllowed);
  }

  private getTextValueToFill(newMaxAllowedMultilines: number, isMergeAllowed: boolean): TextValueVO {
    if ((this.isHasPreviousMergedUsageId || this.isHasNextMergedUsageId) && !isMergeAllowed) {
      return new CompressionSimpleTextValueVO();
    } else if (this.maxAllowedMultilines !== newMaxAllowedMultilines && this.textValue instanceof TableTextValueVO) {
      return newMaxAllowedMultilines === 1 ? this.textValue.rows[0].items[0].value : new CompressionSimpleTextValueVO();
    }

    return this.textValue;
  }
}

@Injectable({ providedIn: 'root' })
export class LabelingArticleConversionDataManager implements OnDestroy {
  public static readonly POINT_TO_SORT_ARTICLES: Vector3 = new Vector3(-10000, 5000, 0);

  private _isConverting: boolean = false;
  private _alertShown: boolean = false;
  private _conversionData: ConversionData[] = [];
  private _stopPostProcess: boolean = false;
  private _convertToMarkedStrip: boolean = false;
  public articleCreationManager: ArticleCreationManager;

  private _addedArticlesSubscription: Subscription

  constructor(
    private alertHelper: AlertHelper,
    private projectMetaElementService: ProjectMetaElementService,
    private selectionModelService: SelectionModelService,
    private textTextureProcessor: TextTextureProcessor,
    private connectionInfoService: ConnectionInfoService,
    private containerService: ContainerService,
    private _articleCreationService: ArticleCreationService,
    private accessoryService: AccessoryService
  ) {
    this.subscribeOnAddedArticles();
  }

  public get isConverting(): boolean {
    return this._isConverting;
  }

  public get stopPostProcess(): boolean {
    return this._stopPostProcess;
  }

  public set stopPostProcess(value: boolean) {
    this._stopPostProcess = value;
  }

  public ngOnDestroy(): void {
    this._addedArticlesSubscription?.unsubscribe();
    this._addedArticlesSubscription = undefined;
  }

  public applyDataToContainerByUsageId(articleContainer: ArticleContainer, usageId: string): void {
    const elements: ConversionData[] = this.getElementsByUsageId(usageId);
    this.applyDataToContainers([articleContainer], elements);
  }

  public applyDataToContainers(articleContainers: ArticleContainer[], elements: ConversionData[] = null): void {
    const textureAreas: TextureAreaUsageVO[] = this.getSortedTextureElementsUsages(articleContainers);
    if (ArrayUtils.isEmpty(textureAreas) || (elements && ArrayUtils.isEmpty(elements))) {
      return;
    }

    for (let i = 0; i < textureAreas.length; i++) {
      const textureAreaUsage: TextureAreaUsageVO = textureAreas[i];
      const element: ConversionData = elements ? elements[i] : this.getElement();
      if (!element) {
        continue;
      }
      const newMaxAllowedMultilines: number = this.getMaxAllowedMultilines(articleContainers, textureAreaUsage);
      const isMergeAllowed: boolean = this.isMergeAllowed(articleContainers, textureAreaUsage);
      if (!this._alertShown) {
        if (
          ((element.isHasPreviousMergedUsageId || element.isHasNextMergedUsageId) && !isMergeAllowed) ||
          (newMaxAllowedMultilines !== element.maxAllowedMultilines &&
            ((newMaxAllowedMultilines > 1 && element.maxAllowedMultilines > 1) ||
              (newMaxAllowedMultilines === 1 && element.textValue instanceof TableTextValueVO)))
        ) {
          this.showAlert();
          this._alertShown = true;
        }
      }
      element.fillTextureAreaUsage(textureAreaUsage, newMaxAllowedMultilines, isMergeAllowed);
      if ((element.isHasPreviousMergedUsageId || element.isHasNextMergedUsageId) && isMergeAllowed) {
        this.fillMergedUsageIds(textureAreaUsage, textureAreas, element);
      }
    }

    this.textTextureProcessor.loadAndApplyTexturesToArticleContainers(articleContainers);
    for (const articleContainer of articleContainers) {
      articleContainer.refreshTextureConnections();
    }
  }

  private getElement(): ConversionData {
    return this._conversionData.splice(0, 1)[0];
  }

  private getElementsByUsageId(usageId: string): ConversionData[] {
    const elements: ConversionData[] = this._conversionData.filter((d) => d.usageIdentity.articleUsageId === usageId);
    if (elements) {
      for (const element of elements) {
        this._conversionData.splice(this._conversionData.indexOf(element), 1);
      }
    }
    return elements;
  }


  private subscribeOnAddedArticles(): void {
    this._addedArticlesSubscription = this._articleCreationService.articlesAdded$.subscribe({
      next: (data) => {
        if (data.type === 'articlesAdded') {
          this.clear();
        }
      },
      error: (error) => Log.error('Error in added articles subscription: ', error)
    })
  }

  private clear(): void {
    if (this._isConverting) {
      this._isConverting = false;
      this._conversionData = [];
      this._alertShown = false;
      this.removeCreationManagerFromContext();
      this.selectionModelService.removeAllSelections();
    }
  }

  private removeCreationManagerFromContext(): void {
    if (this.articleCreationManager) {
      this.articleCreationManager = undefined;
    }
  }

  private getSortedTextureElementsUsages(articleContainers: ArticleContainer[]): TextureAreaUsageVO[] {
    const result: TextureAreaUsageVO[] = [];
    let connectionCubes: Connection[] = [];

    for (const articleContainer of articleContainers) {
      const textureAreas: TextureAreaVO[] = ArticleUtils.getTextTextureElements(articleContainer.article);
      for (const textureAreaVO of textureAreas) {
        let metaElementUsage: MetaElementUsageVO = this.projectMetaElementService.getMetaElementUsage(articleContainer.articleGUID, textureAreaVO.name);
        if (!metaElementUsage) {
          metaElementUsage = this.projectMetaElementService.addMetaElementUsage(articleContainer.articleGUID, textureAreaVO);
        }
        const cube: Connection = this.connectionInfoService.findConnectionByIdentity(metaElementUsage.identity);
        if (!cube.metaElementUsage) {
          cube.metaElementUsage = metaElementUsage;
        }
        connectionCubes.push(cube);
      }
    }

    connectionCubes = ConnectionInfoUtils.sortConnectionsByPoint(connectionCubes);

    for (const connectionCube of connectionCubes) {
      result.push(connectionCube.metaElementUsage as TextureAreaUsageVO);
    }

    return result;
  }

  public async convertToStrip(articleCreationManager: ArticleCreationManager, searchRequirements: ConnectionCubeSearchRequirements[], acceptingCubes: Connection[],
    detachArticleUsageIds: string[]): Promise<void> {
    if (!this._isConverting) {
      return;
    }
    this._convertToMarkedStrip = true;



    this.articleCreationManager = articleCreationManager;
    this.articleCreationManager.detachArticleUsageIds = detachArticleUsageIds;

    try {
      const connectionSearchResult: ConnectionSearchResult = await this.accessoryService.getArticlesByRequirements(searchRequirements).toPromise();
      await this.handleSuccess_allArticlesLoaded(connectionSearchResult)
    } catch (error) {
      Log.error('Could not get article by requirement: ', error)
    }
  }

  public collectData(articleUsageIds: string[]): void {
    this.collectDataFromArticleContainers(this.containerService.findArticleContainersInGlobalByGuids(articleUsageIds));
  }

  private collectDataFromArticleContainers(articleContainers: ArticleContainer[]): void {
    const textureAreaUsages: TextureAreaUsageVO[] = this.getSortedTextureElementsUsages(articleContainers);
    this._conversionData = [];

    for (const textureAreaUsage of textureAreaUsages) {
      const conversionData = ConversionData.createFromTextureAreaUsage(textureAreaUsage);
      conversionData.maxAllowedMultilines = this.getMaxAllowedMultilines(articleContainers, textureAreaUsage);
      this._conversionData.push(conversionData);
    }

    this._isConverting = true;
  }

  private getMaxAllowedMultilines(articleContainers: ArticleContainer[], textureAreaUsage: TextureAreaUsageVO): number {
    const textureArea: MetaElementVO = this.getTextureArea(articleContainers, textureAreaUsage);
    if (textureArea) {
      const multilineCount: number = PropertyUtils.getIntPropertyValue(
        textureArea.behaviorProperties,
        MetaElementBehaviorPropertyDef.ALLOWED_MULTILINES
      );
      return NumberUtils.isNotNumber(multilineCount) ? 1 : multilineCount;
    }
    return 1;
  }

  private isMergeAllowed(articleContainers: ArticleContainer[], textureAreaUsage: TextureAreaUsageVO): boolean {
    const textureArea: MetaElementVO = this.getTextureArea(articleContainers, textureAreaUsage);
    if (textureArea) {
      return PropertyUtils.getBooleanPropertyValue(textureArea.behaviorProperties, MetaElementBehaviorPropertyDef.ALLOW_MERGE);
    }
    return false;
  }

  private getTextureArea(articleContainers: ArticleContainer[], textureAreaUsage: TextureAreaUsageVO): MetaElementVO {
    const articleUsageId: string = textureAreaUsage.identity && textureAreaUsage.identity.articleUsageId;
    if (articleUsageId) {
      const articleContainer: ArticleContainer = articleContainers.find((container) => container.articleGUID === articleUsageId);
      if (articleContainer) {
        const textureArea: MetaElementVO = articleContainer.article.metaElements.find((metaElement) => metaElement instanceof TextureAreaVO);
        if (textureArea) {
          return textureArea;
        }
      }
    }
    return null;
  }

  private showAlert(): void {
    this.alertHelper.showAlert('convert.label.warning.title', 'convert.label.warning.text', undefined, [AlertButton.OK], "error");
  }

  private fillMergedUsageIds(textureAreaUsage: TextureAreaUsageVO, textureAreas: TextureAreaUsageVO[], element: ConversionData): void {
    const index: number = textureAreas.findIndex((textureArea) => textureArea.identity.equals(textureAreaUsage.identity));
    if (index !== -1) {
      if (element.isHasPreviousMergedUsageId && index !== 0) {
        textureAreaUsage.previousMergedUsageId = textureAreas[index - 1].identity;
      }
      if (element.isHasNextMergedUsageId && index < textureAreas.length - 1) {
        textureAreaUsage.nextMergedUsageId = textureAreas[index + 1].identity;
      }
    }
  }

  private async handleSuccess_allArticlesLoaded(connectionSearchResult: ConnectionSearchResult): Promise<void> {
    // handle only for conversion into STRIP
    if (!this._isConverting || !this._convertToMarkedStrip) {
      return;
    }
    this._convertToMarkedStrip = false;

    const suitableConnection: SuitableConnectionsVO = connectionSearchResult.vo.connections[0];

    await this.articleCreationManager.createArticlesBySuitableConnections(suitableConnection, connectionSearchResult.connectionCubeVectors);
  }
}
